"use client";

import { useEffect, useSyncExternalStore } from "react";
import { loadLazyModule } from "src/lib/LazyModules";
import {
  attachToastRuntime,
  isToastRuntimeLoaded,
  loadToasts,
  subscribeToToasts,
} from "./ToastActions";

/**
 * The toast container, rendered by the root route. Nothing is rendered on the server or during
 * hydration; the container mounts once the runtime has loaded.
 */
const Toasts = () => {
  const loaded = useSyncExternalStore(subscribeToToasts, isToastRuntimeLoaded, () => false);

  // Load the runtime once the page is idle, so the first toast shows without waiting for it.
  useEffect(() => {
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(loadToasts, { timeout: 3000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(loadToasts, 1000);
    return () => window.clearTimeout(id);
  }, []);

  // Once the container has mounted, show the toasts that were waiting. A task later: react-toastify's
  // container replaces its toast store as it settles after mounting, and toasts added to the first
  // store were never shown.
  useEffect(() => {
    if (!loaded) return;
    const module = loadLazyModule("toasts").value;
    if (!module) return;
    let detach: (() => void) | undefined;
    const timeout = window.setTimeout(() => {
      detach = attachToastRuntime(module);
    });
    return () => {
      window.clearTimeout(timeout);
      detach?.();
    };
  }, [loaded]);

  if (!loaded) return null;
  const ToastViewport = loadLazyModule("toasts").value?.ToastViewport;
  return ToastViewport ? <ToastViewport /> : null;
};

export default Toasts;
